import { API_BASE_URL, fetchWithCSRF } from '../config';

/**
 * Service class for all report-related API calls
 */
class ReportService {
  /**
   * Save updated report data
   */
  async saveReport(reportData) {
    const response = await fetchWithCSRF(`${API_BASE_URL}/api/universities/reports/save/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reportData)
    });

    if (!response.ok) {
      throw new Error('Failed to save report');
    }

    return response.json();
  }

  /**
   * Load more schools for a category with AI ranking
   */
  async loadMoreSchools({ uuid, category, currentCount, formData, recommendations }) {
    const studentProfile = {
      gpa: formData.gpa,
      sat_score: formData.satScore,
      act_score: formData.actScore,
      grade: formData.grade,
      state: formData.state
    };

    const categorySchools = recommendations[category] || [];
    const interestedSchools = formData.schools
      ? formData.schools.split(',').map(s => s.trim()).filter(s => s.length > 0)
      : [];

    const requestBody = {
      student_profile: studentProfile,
      schools_by_category: {
        [category]: categorySchools
      },
      interested_schools: interestedSchools,
      report_uuid: uuid,
      top_n: currentCount + 9
    };

    const response = await fetchWithCSRF(`${API_BASE_URL}/api/universities/ai/rank-schools/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error('Failed to load more schools');
    }

    return response.json();
  }

  /**
   * Apply AI filter to recommendations
   */
  async applyAIFilter({ uuid, formData, filteredRecommendations }) {
    const studentProfile = {
      gpa: formData.gpa,
      sat_score: formData.satScore,
      act_score: formData.actScore,
      grade: formData.grade,
      state: formData.state
    };

    const schoolsByCategory = {
      reach: filteredRecommendations.reach || [],
      target: filteredRecommendations.target || [],
      safety: filteredRecommendations.safety || []
    };

    const interestedSchools = formData.schools
      ? formData.schools.split(',').map(s => s.trim()).filter(s => s.length > 0)
      : [];

    const rankResponse = await fetchWithCSRF(`${API_BASE_URL}/api/universities/ai/rank-schools/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        student_profile: studentProfile,
        schools_by_category: schoolsByCategory,
        interested_schools: interestedSchools,
        report_uuid: uuid,
        top_n: 9,
        skip_cache: true  // Skip cache when applying filters - schools have changed
      })
    });

    if (!rankResponse.ok) {
      throw new Error('Failed to apply AI filter');
    }

    const rankData = await rankResponse.json();

    // Save the updated report
    const payload = {
      gpa: formData.gpa,
      sat_score: formData.satScore,
      act_score: formData.actScore,
      psat_score: formData.psatScore,
      grade: formData.grade,
      state: formData.state || '',
      schools_interested: formData.schools || '',
      majors: formData.majors || [],
      knows_major: formData.knowsMajor,
      answerer: formData.answerer,
      recommendations: filteredRecommendations
    };

    const saveResponse = await fetchWithCSRF(`${API_BASE_URL}/api/universities/reports/save/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!saveResponse.ok) {
      throw new Error('Failed to save filtered report');
    }

    const savedReport = await saveResponse.json();

    return {
      rankData,
      savedReport
    };
  }

  /**
   * Submit NPS score (and optional feedback comment) for a report
   */
  async submitNPSScore({ score, comment, reportUuid }) {
    const response = await fetchWithCSRF(`${API_BASE_URL}/api/universities/reports/${reportUuid}/nps/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        score,
        ...(comment && { comment }),
      })
    });

    if (!response.ok) {
      throw new Error('Failed to submit NPS score');
    }

    return response.json();
  }
}

const reportService = new ReportService();
export default reportService;